import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Github, Wand2, RefreshCw, Search } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function RepoToAppleDialog({ open, onOpenChange, onBlueprint }) {
  const [repoUrl, setRepoUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [repos, setRepos] = useState(null); // null = haven't tried; [] = empty

  useEffect(() => {
    if (!open) return;
    setRepoUrl("");
    setNotes("");
    api.get("/github/repos").then(({ data }) => setRepos(data)).catch(() => setRepos([]));
  }, [open]);

  const submit = async () => {
    if (!repoUrl.trim()) return toast.error("Paste a repo URL or pick one below");
    setBusy(true);
    try {
      const { data } = await api.post("/prompt/from-repo", {
        repo_url: repoUrl.trim(),
        extra_notes: notes.trim(),
      });
      toast.success("Apple blueprint ready");
      onBlueprint?.(data);
      onOpenChange(false);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error(err.response?.data?.detail || "You've used all your prompts this month.");
      } else {
        toast.error(err.response?.data?.detail || "Could not import that repo.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream border-2 border-ink rounded-3xl max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="repo-to-apple-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
            <Github className="w-5 h-5 text-marigold" /> Convert a GitHub repo → Apple app
          </DialogTitle>
          <DialogDescription className="text-ink2">
            Paste any GitHub repo (web app, mobile app, FastAPI service). We'll read the source, mirror its
            screens & data model in SwiftUI, and bundle the original Python backend into your starter kit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div>
            <div className="label-eyebrow mb-2">Repo URL or owner/name</div>
            <div className="flex items-center gap-2 card-soft px-3 py-2">
              <Search className="w-4 h-4 text-ink2 flex-shrink-0" />
              <input
                type="text"
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                placeholder="vercel/next.js  or  https://github.com/owner/repo"
                className="w-full bg-transparent outline-none text-sm"
                data-testid="repo-url-input"
              />
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-2">Extra notes for the converter (optional)</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={400}
              placeholder="e.g. focus on the dashboard pages; keep the Stripe routes; add a SwiftUI-native onboarding."
              className="w-full card-soft p-3 text-sm bg-transparent outline-none resize-none"
              data-testid="repo-notes-input"
            />
          </div>

          {Array.isArray(repos) && repos.length > 0 && (
            <div data-testid="repo-picker-list">
              <div className="label-eyebrow mb-2">Or pick from your repos</div>
              <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
                {repos.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRepoUrl(r.full_name)}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition ${
                      repoUrl === r.full_name
                        ? "border-marigold bg-marigold/5 text-ink"
                        : "border-border hover:border-ink"
                    }`}
                    data-testid={`repo-picker-${r.full_name.replace("/", "-")}`}
                  >
                    <div className="font-mono truncate">{r.full_name}</div>
                    {r.private && <span className="text-[10px] uppercase tracking-wider text-ink2">private</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {repos !== null && repos.length === 0 && (
            <div className="text-sm text-ink2" data-testid="repo-picker-empty">
              Connect GitHub on your dashboard to pick from your repos directly. (Public repos still work via URL.)
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button
              onClick={() => onOpenChange(false)}
              className="text-sm px-4 py-2 rounded-full border border-border text-ink2 hover:border-ink hover:text-ink"
              data-testid="repo-import-cancel"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={busy}
              className="btn-primary text-sm py-2 disabled:opacity-40"
              data-testid="repo-import-submit"
            >
              {busy ? <><RefreshCw className="w-4 h-4 animate-spin" /> Converting…</> : <><Wand2 className="w-4 h-4" /> Convert to Apple app</>}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
